import type { Goal, Game, Profile } from '../types';

// Get configuration from environment variables
const API_SECRET_KEY = import.meta.env.VITE_API_KEY;
const BACKEND_URL = import.meta.env.VITE_CHAT_URL;

// Define OpenAI types locally (no need to import the entire OpenAI package)
interface ChatCompletionMessageParam {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ChatCompletionParams {
  model: string;
  messages: ChatCompletionMessageParam[];
  max_tokens?: number;
  temperature?: number;
  top_p?: number;
  n?: number;
  stream?: boolean;
  stop?: string | string[];
  presence_penalty?: number;
  frequency_penalty?: number;
}

// Initialize OpenAI with API key from environment
const openai = {
  chat: {
    completions: {
      create: async (params: ChatCompletionParams) => {
        const response = await fetch(BACKEND_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_SECRET_KEY,
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new Error('Failed to get AI response');
        }

        const data = await response.json();
        return {
          ...data,
          choices: data.choices.map((choice: any) => ({
            ...choice,
            message: {
              ...choice.message,
              content: choice.message.content || '',
            },
          })),
        };
      },
    },
  },
};

// Drill database - you can expand this based on your needs
const DRILL_DATABASE = [
  {
    id: 'lateral-movement-1',
    title: 'Lateral Movement Drill',
    baseDescription:
      'Practice moving side to side across the crease to improve positioning for shots from different angles.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/dd42beea28584502b476d107dcaeea16.mov',
    zones: ['top-left', 'top-right', 'middle-left', 'middle-right'],
    goalTypes: ['Wrist Shot', 'Snap Shot', 'One-Timer'],
  },
  {
    id: 'butterfly-technique-1',
    title: 'Butterfly Technique Drill',
    baseDescription:
      'Focus on proper butterfly positioning and recovery for low shots.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/19cde1d9f6bf4bce8c5241ec8be49490.mov',
    zones: ['bottom-left', 'bottom-right', 'bottom-center'],
    goalTypes: ['Wrist Shot', 'Backhand', 'Deflection / Tip-in'],
  },
  {
    id: 'high-shot-positioning-1',
    title: 'High Shot Positioning',
    baseDescription:
      'Work on positioning and glove/blocker saves for high shots.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/6b7a766387e24147aebc16b44b45cbff.mov',
    zones: ['top-left', 'top-right', 'top-center'],
    goalTypes: ['Slap Shot', 'Snap Shot', 'One-Timer'],
  },
  {
    id: 'rebound-control-1',
    title: 'Rebound Control Drill',
    baseDescription:
      'Practice controlling rebounds and second-chance opportunities.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/18f2ec3b421e423199a4ecd37930d7ff.mov',
    zones: ['middle-center', 'bottom-center'],
    goalTypes: ['Rebound Shot', 'Deflection / Tip-in'],
  },
  {
    id: 'breakaway-technique-1',
    title: 'Breakaway Technique',
    baseDescription: 'Master the fundamentals of stopping breakaway attempts.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/4c6eaa33c2b643c18a191afc77fbe056.mov',
    zones: ['all'],
    goalTypes: ['all'],
    situations: ['Break away', 'Penalty Shot'],
  },
  {
    id: 'angle-play-1',
    title: 'Angle Play Fundamentals',
    baseDescription:
      'Improve your positioning and angle cutting to reduce shooting angles.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/2a028e7474f045f3b309a54462727ece.mov',
    zones: ['top-left', 'top-right', 'middle-left', 'middle-right'],
    goalTypes: ['Wrist Shot', 'Snap Shot', 'Slap Shot'],
  },
  {
    id: 'screen-shots-1',
    title: 'Playing Through Screens',
    baseDescription:
      'Learn to track pucks through traffic and maintain positioning.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/004340bae52a42fcbecdd181d0a8acc4.mp4',
    zones: ['all'],
    goalTypes: ['Deflection / Tip-in', 'Screened Shot'],
    situations: ['Screened Shot', 'Net-Front Battle'],
  },
  {
    id: 'post-play-1',
    title: 'Post Play and Recovery',
    baseDescription:
      'Improve positioning and recovery when playing behind the net.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/ad70320e53c34cbd9c865b1687b38c30.mov',
    zones: ['bottom-left', 'bottom-right'],
    goalTypes: ['Wraparound', 'Backhand'],
  },
  {
    id: 'cross-ice-tracking-1',
    title: 'Cross-Ice Tracking',
    baseDescription:
      'Develop skills for tracking passes and shots across the ice.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/19cde1d9f6bf4bce8c5241ec8be49490.mov',
    zones: ['top-left', 'top-right', 'middle-left', 'middle-right'],
    goalTypes: ['One-Timer', 'Cross-Ice Pass'],
    situations: ['Cross-Ice Pass', 'Odd-Man Rush'],
  },
  {
    id: 'five-hole-coverage-1',
    title: 'Five-Hole Coverage',
    baseDescription:
      'Focus on sealing the five-hole and improving butterfly technique.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/19cde1d9f6bf4bce8c5241ec8be49490.mov',
    zones: ['bottom-center', 'middle-center'],
    goalTypes: ['Wrist Shot', 'Snap Shot', 'Backhand'],
  },
  {
    id: 'glove-hand-positioning-1',
    title: 'Glove Hand Positioning',
    baseDescription:
      'Improve glove positioning and reaction time for high shots.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/89783f9b649e47f5bde49118a05ca1ec.mov',
    zones: ['top-left', 'middle-left'],
    goalTypes: ['Wrist Shot', 'Snap Shot', 'Slap Shot'],
  },
  {
    id: 'blocker-side-technique-1',
    title: 'Blocker Side Technique',
    baseDescription: 'Enhance blocker positioning and rebound control.',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/18f2ec3b421e423199a4ecd37930d7ff.mov',
    zones: ['top-right', 'middle-right'],
    goalTypes: ['Slap Shot', 'One-Timer', 'Snap Shot'],
  },
  {
    id: 'net-front-battle',
    title: 'Net Front Battle Low',
    baseDescription:
      'Learn to track pucks through traffic in front of the net when pucks come low',
    videoUrl: 'https://cdn.shopify.com/videos/c/o/v/c593cbd273d1462995f39668285d2776.mov',
    zones: ['bottom-center', 'bottom-left', 'bottom-right'],
    goalTypes: ['Deflection / Tip-in', 'Screened Shot', 'Snap Shot', 'Rebound Shot'],
    situations: ['Screened Shot', 'Net-Front Battle'],
  },
];

function analyzeGoalZones(goals: Goal[]) {
  const zones = {
    'top-left': 0,
    'top-center': 0,
    'top-right': 0,
    'middle-left': 0,
    'middle-center': 0,
    'middle-right': 0,
    'bottom-left': 0,
    'bottom-center': 0,
    'bottom-right': 0,
  };

  goals.forEach((goal) => {
    const x = goal.xPosition;
    const y = goal.yPosition;

    let zone = '';

    // Determine vertical zone
    if (y <= 33) zone += 'top-';
    else if (y <= 66) zone += 'middle-';
    else zone += 'bottom-';

    // Determine horizontal zone
    if (x <= 33) zone += 'left';
    else if (x <= 66) zone += 'center';
    else zone += 'right';

    if (zones[zone as keyof typeof zones] !== undefined) {
      zones[zone as keyof typeof zones]++;
    }
  });

  return zones;
}

async function generateDrillDescription(
  drill: any,
  goals: Goal[],
  zones: any,
  profile: Profile | null,
  language: 'en' | 'fr' = 'en'
) {
  const playerName = profile ? profile.firstName : 'Player';
  const playerLevel = profile?.level || 'your level';

  // Analyze specific issues from the goals
  const relevantGoals = goals.filter((goal) => {
    const x = goal.xPosition;
    const y = goal.yPosition;

    let goalZone = '';
    if (y <= 33) goalZone += 'top-';
    else if (y <= 66) goalZone += 'middle-';
    else goalZone += 'bottom-';

    if (x <= 33) goalZone += 'left';
    else if (x <= 66) goalZone += 'center';
    else goalZone += 'right';

    return drill.zones.includes(goalZone) || drill.zones.includes('all');
  });

  const shotTypes = relevantGoals.map((g) => g.shotType).filter(Boolean);
  const situations = relevantGoals.map((g) => g.gameSituation).filter(Boolean);
  const descriptions = relevantGoals.map((g) => g.situationDescription);

  const prompts = {
    en: `You are a professional goalie coach creating a personalized drill description for ${playerName}, a ${playerLevel} goalie.

DRILL: ${drill.title}
BASE DESCRIPTION: ${drill.baseDescription}

SPECIFIC ISSUES IDENTIFIED:
- Goals conceded in relevant zones: ${relevantGoals.length}
- Shot types that scored: ${shotTypes.join(', ') || 'Various'}
- Game situations: ${situations.join(', ') || 'Various'}
- Specific goal descriptions: ${
      descriptions.slice(0, 2).join('; ') || 'General positioning issues'
    }

Please write a personalized drill description (2-3 sentences max) that:
1. Explains WHY this drill is specifically recommended for this player
2. Addresses the specific weaknesses identified from their goals
3. Is encouraging and actionable
4. Mentions the specific areas they need to improve based on their goal data

Keep it concise, specific to their performance data, and motivational. Don't use the player's name in the description.`,
    
    fr: `Vous êtes un entraîneur professionnel de gardiens de but créant une description d'exercice personnalisée pour ${playerName}, un gardien de niveau ${playerLevel}.

EXERCICE: ${drill.title}
DESCRIPTION DE BASE: ${drill.baseDescription}

PROBLÈMES SPÉCIFIQUES IDENTIFIÉS:
- Buts accordés dans les zones pertinentes: ${relevantGoals.length}
- Types de tirs qui ont marqué: ${shotTypes.join(', ') || 'Variés'}
- Situations de jeu: ${situations.join(', ') || 'Variées'}
- Descriptions spécifiques des buts: ${
      descriptions.slice(0, 2).join('; ') || 'Problèmes de positionnement généraux'
    }

Veuillez écrire une description d'exercice personnalisée (2-3 phrases maximum) qui:
1. Explique POURQUOI cet exercice est spécifiquement recommandé pour ce joueur
2. Aborde les faiblesses spécifiques identifiées à partir de ses buts
3. Est encourageante et pratique
4. Mentionne les domaines spécifiques qu'il doit améliorer basés sur ses données de buts

Gardez-la concise, spécifique à ses données de performance, et motivante. N'utilisez pas le nom du joueur dans la description.`
  };
  
  const prompt = prompts[language];

  try {
    const completion = await openai.chat.completions.create({
      model: 'gpt-4',
      messages: [
        {
          role: 'system',
          content: language === 'fr' ?
            "Vous êtes un entraîneur professionnel de gardiens de but. Rédigez des descriptions d'exercices concises, spécifiques et encourageantes qui abordent les problèmes de performance réels du joueur. Limitez les descriptions à 2-3 phrases maximum." :
            "You are a professional goalie coach. Write concise, specific, and encouraging drill descriptions that address the player's actual performance issues. Keep descriptions to 2-3 sentences maximum.",
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      max_tokens: 150,
      temperature: 0.7,
    });

    return completion.choices[0]?.message?.content || drill.baseDescription;
  } catch (error) {
    console.error('Error generating drill description:', error);
    return drill.baseDescription;
  }
}

async function getRecommendedDrills(
  goals: Goal[],
  zones: any,
  profile: Profile | null,
  language: 'en' | 'fr' = 'en'
) {
  // Get the top 3 zones where goals were scored
  const sortedZones = Object.entries(zones)
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3)
    .map(([zone]) => zone);

  // Filter drills that target these zones
  const relevantDrills = DRILL_DATABASE.filter((drill) => {
    return drill.zones.some(
      (zone) => sortedZones.includes(zone) || zone === 'all'
    );
  }).slice(0, 3); // <-- limite à 3 drills max

  // Get personalized descriptions for each drill
  const drillsWithDescriptions = await Promise.all(
    relevantDrills.map(async (drill) => {
      const description = await generateDrillDescription(
        drill,
        goals,
        zones,
        profile,
        language
      );
      return { ...drill, description };
    })
  );

  return drillsWithDescriptions;
}

async function generateAIAnalysis(
  games: Game[],
  goals: Goal[],
  profile: Profile | null,
  language: 'en' | 'fr' = 'en'
) {
  try {
    const zones = analyzeGoalZones(goals);
    
    const prompts = {
      en: `You are a professional goalie coach analyzing ${goals.length} goals from ${games.length} game(s).

Player Info:
- Level: ${profile?.level || 'Unknown'}
- Position: Goalie
- Goals Analyzed: ${goals.length}

Goal Data:
${goals
  .map(
    (goal, index) => `
Goal ${index + 1}:
- Shot Type: ${goal.shotType || 'Unknown'}
- Game Situation: ${goal.gameSituation || 'Unknown'}
- Description: ${goal.situationDescription || 'No description available'}
`
  )
  .join('\n')}

Please provide a comprehensive analysis (4-5 paragraphs) that:
1. Identifies patterns in the goals conceded
2. Highlights specific technical or positional issues
3. Notes any situational challenges (e.g., breakaways, power plays)
4. Provides actionable advice for improvement

Keep the tone professional but encouraging. Focus on constructive feedback and specific areas for improvement.`,
      
      fr: `Vous êtes un entraîneur professionnel de gardiens de but analysant ${goals.length} buts de ${games.length} match(s).

Informations du Joueur:
- Niveau: ${profile?.level || 'Inconnu'}
- Position: Gardien de but
- Buts Analysés: ${goals.length}

Données des Buts:
${goals
  .map(
    (goal, index) => `
But ${index + 1}:
- Type de Tir: ${goal.shotType || 'Inconnu'}
- Situation de Jeu: ${goal.gameSituation || 'Inconnue'}
- Description: ${goal.situationDescription || 'Aucune description disponible'}
`
  )
  .join('\n')}

Veuillez fournir une analyse complète (4-5 paragraphes) qui:
1. Identifie les tendances dans les buts accordés
2. Met en évidence les problèmes techniques ou de positionnement spécifiques
3. Note les défis situationnels (ex: échappées, avantages numériques)
4. Fournit des conseils pratiques pour l'amélioration

Gardez un ton professionnel mais encourageant. Concentrez-vous sur des commentaires constructifs et des domaines spécifiques d'amélioration.`
    };
    
    const prompt = prompts[language];

    const completion = await openai.chat.completions.create({
      model: 'gpt-4',
      messages: [
        {
          role: 'system',
          content:
            'You are a professional goalie coach providing detailed game analysis. Be specific, constructive, and encouraging in your feedback.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
    });

    return completion.choices[0]?.message?.content || '';
  } catch (error) {
    console.error('Error generating AI analysis:', error);
    return 'Unable to generate analysis at this time. Please try again later.';
  }
}

function generateTips(goals: Goal[], zones: Record<string, number>): string[] {
  const tips: string[] = [];

  // Add zone-based tips
  const problemZones = Object.entries(zones)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([zone]) => zone);

  if (problemZones.length > 0) {
    tips.push(
      `Focus on improving positioning for shots to the ${problemZones.join(
        ' and '
      )} areas.`
    );
  }

  // Add shot type based tips
  const shotTypes = goals.reduce((acc, goal) => {
    if (goal.shotType) {
      acc[goal.shotType] = (acc[goal.shotType] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const commonShotTypes = Object.entries(shotTypes)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([type]) => type);

  if (commonShotTypes.length > 0) {
    tips.push(
      `Practice specifically against ${commonShotTypes.join(' and ')} shots.`
    );
  }

  // Add situation-based tips
  const situations = goals.reduce((acc, goal) => {
    if (goal.gameSituation) {
      acc[goal.gameSituation] = (acc[goal.gameSituation] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const commonSituations = Object.entries(situations)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([situation]) => situation);

  if (commonSituations.length > 0) {
    tips.push(
      `Work on improving performance in ${commonSituations.join(
        ' and '
      )} situations.`
    );
  }

  return tips;
}

function generateMotivationalNote(
  profile: Profile | null,
  goals: Goal[],
  language: 'en' | 'fr' = 'en'
): string {
  const playerName = profile ? profile.firstName : 'Player';
  const playerLevel = profile?.level || 'your level';
  const totalGoals = goals.length;

  const messages = {
    en: `Keep working hard, ${playerName}! Every goal is a learning opportunity. Focus on the areas we've identified, and you'll continue to improve at ${playerLevel}. Remember, even the best goalies face challenges - it's how we respond that matters.`,
    fr: `Continue à bien travailler, ${playerName}! Chaque but est une opportunité d'apprentissage. Concentre-toi sur les domaines que nous avons identifiés, et tu continueras à t'améliorer au niveau ${playerLevel}. Rappelle-toi, même les meilleurs gardiens font face à des défis - c'est notre façon de réagir qui compte.`
  };
  
  return messages[language];
}

export async function analyzeGamesWithAI(
  games: Game[],
  goals: Goal[],
  profile: Profile | null,
  language: 'en' | 'fr' = 'en'
) {
  try {
    const zones = analyzeGoalZones(goals);
    const analysis = await generateAIAnalysis(games, goals, profile, language);
    const drills = await getRecommendedDrills(goals, zones, profile, language);
    const tips = generateTips(goals, zones);
    const motivationalNote = generateMotivationalNote(profile, goals, language);

    return {
      analysis,
      drills,
      tips,
      motivationalNote,
    };
  } catch (error) {
    console.error('Error in AI analysis:', error);
    throw error;
  }
}
